// third-party
import { createSlice } from '@reduxjs/toolkit';

// types
import { NotificationSliceProps, NotificationByUser, NotificationFilters, PaginationInput } from 'types/notification';

// ----------------------------------------------------------------------

const initialState: NotificationSliceProps = {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
    lastFetched: null
};

// ==============================|| SLICE - NOTIFICATION ||============================== //

const slice = createSlice({
    name: 'notification',
    initialState,
    reducers: {
        hasError(state, action) {
            state.error = action.payload;
        },

        setLoading(state, action) {
            state.loading = action.payload;
        },

        getNotificationsSuccess(state, action) {
            state.notifications = action.payload.notifications;
            state.unreadCount = action.payload.unreadCount;
            state.lastFetched = Date.now();
            state.loading = false;
        },

        getUnreadCountSuccess(state, action) {
            state.unreadCount = action.payload;
        },

        markNotificationAsReadSuccess(state, action) {
            const index = state.notifications.findIndex((n) => n.message.id === action.payload.message.id);
            if (index !== -1) {
                state.notifications[index] = action.payload;
                state.unreadCount = state.notifications.filter((n) => n.readAt === null).length;
            }
        },

        markAllNotificationsAsReadSuccess(state) {
            const now = Date.now();
            state.notifications = state.notifications.map((n) => ({
                ...n,
                readAt: n.readAt || now
            }));
            state.unreadCount = 0;
        },

        addNotification(state, action) {
            state.notifications.unshift(action.payload);
            if (!action.payload.readAt) {
                state.unreadCount += 1;
            }
        },

        togglePinSuccess(state, action) {
            const index = state.notifications.findIndex((n) => n.message.id === action.payload);
            if (index !== -1) {
                state.notifications[index].pinned = !state.notifications[index].pinned;
            }
        },

        toggleMuteSuccess(state, action) {
            const index = state.notifications.findIndex((n) => n.message.id === action.payload);
            if (index !== -1) {
                state.notifications[index].muted = !state.notifications[index].muted;
            }
        }
    }
});

// Reducer
export default slice.reducer;

// Actions
export const {
    hasError,
    setLoading,
    getNotificationsSuccess,
    getUnreadCountSuccess,
    markNotificationAsReadSuccess,
    markAllNotificationsAsReadSuccess,
    addNotification,
    togglePinSuccess,
    toggleMuteSuccess
} = slice.actions;

// ----------------------------------------------------------------------

export function getNotifications(filters?: NotificationFilters, pagination?: PaginationInput) {
    return async (dispatch: any, getState: any) => {
        try {
            dispatch(setLoading(true));

            await new Promise((resolve) => setTimeout(resolve, 300));

            const state = getState();
            let notifications = [...state.notification.notifications];

            if (filters?.read !== undefined) {
                notifications = notifications.filter((n) => {
                    const isRead = n.readAt !== null;
                    return isRead === filters.read;
                });
            }
            if (filters?.priority !== undefined) {
                notifications = notifications.filter((n) => n.message.priority === filters.priority);
            }
            if (filters?.important !== undefined) {
                notifications = notifications.filter((n) => n.important === filters.important);
            }
            if (filters?.startDate !== undefined) {
                notifications = notifications.filter((n) => n.message.createdAt >= filters.startDate!);
            }
            if (filters?.endDate !== undefined) {
                notifications = notifications.filter((n) => n.message.createdAt <= filters.endDate!);
            }

            const limit = pagination?.limit || 50;
            const offset = pagination?.offset || 0;
            const paginatedNotifications = notifications.slice(offset, offset + limit);

            const unreadCount = notifications.filter((n) => n.readAt === null).length;

            dispatch(
                getNotificationsSuccess({
                    notifications: paginatedNotifications,
                    unreadCount
                })
            );
        } catch (error) {
            dispatch(hasError(error));
            dispatch(setLoading(false));
        }
    };
}

export function getUnreadCount() {
    return async (dispatch: any, getState: any) => {
        try {
            await new Promise((resolve) => setTimeout(resolve, 300));

            const state = getState();
            const unreadCount = state.notification.notifications.filter((n: NotificationByUser) => n.readAt === null).length;

            dispatch(getUnreadCountSuccess(unreadCount));
        } catch (error) {
            dispatch(hasError(error));
        }
    };
}

export function markNotificationAsRead(notificationId: number) {
    return async (dispatch: any, getState: any) => {
        try {
            await new Promise((resolve) => setTimeout(resolve, 300));

            const state = getState();
            const notifications = [...state.notification.notifications];
            const notification = notifications.find((n) => n.message.id === notificationId);

            if (!notification) {
                throw new Error(`Notification ${notificationId} not found`);
            }

            const updatedNotification = {
                ...notification,
                readAt: Date.now()
            };

            const index = notifications.findIndex((n) => n.message.id === notificationId);
            if (index !== -1) {
                notifications[index] = updatedNotification;
            }

            dispatch(markNotificationAsReadSuccess(updatedNotification));
            dispatch(getUnreadCountSuccess(notifications.filter((n) => n.readAt === null).length));
        } catch (error) {
            dispatch(hasError(error));
        }
    };
}

export function markAllNotificationsAsRead() {
    return async (dispatch: any, getState: any) => {
        try {
            await new Promise((resolve) => setTimeout(resolve, 300));

            dispatch(markAllNotificationsAsReadSuccess());
        } catch (error) {
            dispatch(hasError(error));
        }
    };
}

export function toggleNotificationPin(notificationId: number) {
    return async (dispatch: any) => {
        try {
            await new Promise((resolve) => setTimeout(resolve, 300));

            dispatch(togglePinSuccess(notificationId));
        } catch (error) {
            dispatch(hasError(error));
        }
    };
}

export function toggleNotificationMute(notificationId: number) {
    return async (dispatch: any) => {
        try {
            await new Promise((resolve) => setTimeout(resolve, 300));

            dispatch(toggleMuteSuccess(notificationId));
        } catch (error) {
            dispatch(hasError(error));
        }
    };
}
